import { useLazyQuery } from '@apollo/client'

import { APIStatus } from '@flowwai-frontend/shared/api-common'

import {
   convertToErrorType,
   getAPIStatusFromNetworkStatus
} from '@flowwai-frontend/bps-officer-web/common/data-access'
import { NETWORK_ONLY_FETCH_POLICY } from '@flowwai-frontend/bps-officer-web/graphql'

import { SearchResultType } from '../../../types'

import {
   GlobalSearchPipelineItemsQuery as Query,
   GlobalSearchPipelineItemsDocument as QueryDocument,
   GlobalSearchPipelineItemsQueryVariables as QueryVariables
} from './query.generatedTypes'
import useAPIResponseHandler from './responseHandler'

interface TriggerAPIArgsType {
   requestObj: QueryVariables['params']
   onSuccess?: () => void
   onFailure?: (error: Error) => void
}

interface APIHookReturnType {
   apiError: Error | null
   apiStatus: APIStatus

   triggerAPI: (args: TriggerAPIArgsType) => void
   fetchMore: (args: TriggerAPIArgsType) => void
   searchResults: SearchResultType[]
   totalCount: number | null
}

const useGetGlobalSearchPipelineItems = (): APIHookReturnType => {
   const [
      getSearchResults,
      { data, error, networkStatus, fetchMore }
   ] = useLazyQuery<Query, QueryVariables>(QueryDocument, {
      fetchPolicy: NETWORK_ONLY_FETCH_POLICY
   })

   const { searchResults, totalCount } = useAPIResponseHandler(data)

   const triggerAPI = async (args: TriggerAPIArgsType) => {
      await getSearchResults({
         variables: { params: args.requestObj },
         onCompleted: args.onSuccess,
         onError: args.onFailure
      })
   }

   const fetchMoreSearchResults = async (args: TriggerAPIArgsType) => {
      await fetchMore({
         variables: { params: args.requestObj }
      })
   }

   return {
      apiError: convertToErrorType(error),
      apiStatus: getAPIStatusFromNetworkStatus(networkStatus, data),
      searchResults,
      fetchMore: fetchMoreSearchResults,
      totalCount,
      triggerAPI
   }
}

export default useGetGlobalSearchPipelineItems
